
import React, { useState, useEffect } from 'react';
import { Button, Form, Container, Card, Alert, ListGroup, Row, Col, Modal, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import AuthLogin, { UserInfo as AuthUserInfo } from './AuthLogin';

interface League {
  id: string;
  name: string;
  season: number;
  active: boolean;
  readyToDraft?: boolean;
}

interface UserInfo {
  userName: string;
  survivorName: string;
  realName: string;
  email: string;
  leagueId: string;
  paymentMethod: string;
  paymentStatus?: 'pending' | 'marked_paid' | 'verified';
}

interface FoundUser {
  userName: string;
  survivorName: string;
  realName: string;
  leagueId: string;
  leagueName: string;
  season: number;
  paymentStatus?: 'pending' | 'marked_paid' | 'verified';
}

interface Props {
  setUserInfo: React.Dispatch<React.SetStateAction<UserInfo | null>>;
  userInfo: UserInfo | null;
}

const VENMO_USERNAME = 'Goldberg-Jonathan';
const PAYPAL_USERNAME = 'jonathangoldberg1';
const CASHAPP_TAG = 'goldbergjontg';
const ENTRY_FEE = 11;

const LeagueJoin: React.FC<Props> = ({ setUserInfo, userInfo }) => {
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [survivorName, setSurvivorName] = useState('');
  const [realName, setRealName] = useState('');
  const [email, setEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Venmo');
  const [leagues, setLeagues] = useState<League[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userEntries, setUserEntries] = useState<FoundUser[]>([]);
  const [availableSeasons, setAvailableSeasons] = useState<number[]>([51, 50, 49]);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    const requested = Number(new URLSearchParams(window.location.search).get('season'));
    if (Number.isInteger(requested) && requested > 0) return requested;
    return 51; // Default to upcoming Season 51
  });

  // Payment Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState<{
    leagueId: string;
    leagueName: string;
    userName: string;
    email: string;
    season: number;
    amount: number;
    paymentMethod: string;
  } | null>(null);
  const [isReportingPayment, setIsReportingPayment] = useState(false);
  const [paymentReported, setPaymentReported] = useState(false);

  const navigate = useNavigate();
  const requestedSeason = Number(new URLSearchParams(window.location.search).get('season'));
  const loggedInSeason = selectedSeason;
  const isPreviewingAnotherSeason = Number.isInteger(requestedSeason) && requestedSeason > 0 && requestedSeason !== loggedInSeason;

  const fetchLeagues = async (season: number, preferredLeagueId?: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
      if (!response.ok) throw new Error('Failed to fetch leagues');
      const data = await response.json();
      const allLeagues = data.leagues || [];
      
      // Filter by season for both joining and finding
      const seasonLeagues = allLeagues.filter((l: any) => (l.season || 49) === season);
      
      // For JOINING, only show ACTIVE leagues
      const activeLeagues = seasonLeagues.filter((l: any) => l.active !== false || l.readyToDraft);
      setLeagues(activeLeagues);
      if (preferredLeagueId && activeLeagues.some((league: League) => league.id === preferredLeagueId)) {
        setSelectedLeagueId(preferredLeagueId);
      }
    } catch (err: any) {
      setError(err.message || 'Could not load leagues.');
      setLeagues([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchSeasonsAndInit = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/seasons`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data.seasons) && data.seasons.length > 0) {
            const sorted = [...data.seasons].sort((a: number, b: number) => b - a);
            setAvailableSeasons(sorted);
            
            const query = new URLSearchParams(window.location.search);
            const requested = Number(query.get('season'));
            const targetSeason = Number.isInteger(requested) && requested > 0 ? requested : Math.max(...sorted);
            setSelectedSeason(targetSeason);
            
            const requestedLeagueId = query.get('leagueId') || undefined;
            fetchLeagues(targetSeason, requestedLeagueId);

            const userKey = `fantasyUser_${targetSeason}`;
            const storedUser = localStorage.getItem(userKey);
            if (storedUser) {
              try {
                setUserInfo(JSON.parse(storedUser));
              } catch (e) {
                // fall back to global
              }
            } else {
              const globalUser = localStorage.getItem('fantasyUser');
              if (globalUser) {
                try {
                  setUserInfo(JSON.parse(globalUser));
                } catch (e) {
                  // ignore
                }
              }
            }
            return;
          }
        }
      } catch (err) {
        console.error('Error loading seasons in LeagueJoin:', err);
      }

      // Fallback
      const query = new URLSearchParams(window.location.search);
      const requested = Number(query.get('season'));
      const targetSeason = Number.isInteger(requested) && requested > 0 ? requested : 51;
      setSelectedSeason(targetSeason);
      fetchLeagues(targetSeason);
    };

    fetchSeasonsAndInit();

    const handleSeasonChange = () => {
      const currentSeason = parseInt(localStorage.getItem('selectedSeason') || '51');
      setSelectedSeason(currentSeason);
      fetchLeagues(currentSeason);
    };

    window.addEventListener('seasonChanged', handleSeasonChange);
    return () => window.removeEventListener('seasonChanged', handleSeasonChange);
  }, [setUserInfo]);

  const handleSeasonSelect = (season: number) => {
    setSelectedSeason(season);
    localStorage.setItem('selectedSeason', season.toString());
    fetchLeagues(season);
    
    // Look up existing season user info or maintain active user login session
    const userKey = `fantasyUser_${season}`;
    const storedUser = localStorage.getItem(userKey);
    let resolvedUser: UserInfo | null = null;

    if (storedUser) {
      try {
        resolvedUser = JSON.parse(storedUser);
      } catch (e) {
        // fallback
      }
    }

    if (!resolvedUser) {
      const matchInEntries = userEntries.find(e => Number(e.season) === Number(season));
      if (matchInEntries) {
        resolvedUser = {
          userName: matchInEntries.userName,
          survivorName: matchInEntries.survivorName || matchInEntries.userName,
          realName: matchInEntries.realName || userInfo?.realName || '',
          leagueId: matchInEntries.leagueId,
          email: userInfo?.email || '',
          paymentMethod: 'Unknown',
          paymentStatus: matchInEntries.paymentStatus
        };
        localStorage.setItem(userKey, JSON.stringify(resolvedUser));
      } else if (userInfo) {
        resolvedUser = {
          ...userInfo,
          leagueId: '' // Not yet joined a league for this target season
        };
      } else {
        const globalUser = localStorage.getItem('fantasyUser');
        if (globalUser) {
          try {
            const parsed = JSON.parse(globalUser);
            resolvedUser = {
              ...parsed,
              leagueId: ''
            };
          } catch (e) {
            // keep null
          }
        }
      }
    }

    if (resolvedUser) {
      setUserInfo(resolvedUser);
    }
    window.dispatchEvent(new Event('seasonChanged'));
  };

  useEffect(() => {
    if (userInfo) {
      if (userInfo.email && !email) setEmail(userInfo.email);
      if (userInfo.realName && !realName) setRealName(userInfo.realName);
      if (userInfo.survivorName && !survivorName) setSurvivorName(userInfo.survivorName);
    }
  }, [userInfo]);

  useEffect(() => {
    if (!userInfo?.email) {
      setUserEntries([]);
      return;
    }
    const fetchUserEntries = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/by-email/${encodeURIComponent(userInfo.email)}`);
        if (!response.ok) throw new Error('Failed to load your entries');
        const data = await response.json();
        setUserEntries(data.users || []);
      } catch (err) {
        console.error('Failed to load logged-in user entries:', err);
        setUserEntries([]);
      }
    };
    fetchUserEntries();
  }, [userInfo?.email]);

  const handleJoinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLeagueId || !survivorName.trim() || !realName.trim() || !email.trim() || !paymentMethod) {
      setError('All fields are required to join.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setIsLoading(true); setError(null); setSuccess(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userName: survivorName, 
          survivorName, 
          realName, 
          email, 
          paymentMethod,
          paymentAmount: ENTRY_FEE
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to join league');
      const leagueName = leagues.find(l => l.id === selectedLeagueId)?.name || '';
      const newUserInfo = { 
        userName: survivorName, 
        survivorName, 
        realName, 
        email, 
        leagueId: selectedLeagueId, 
        paymentMethod,
        paymentStatus: 'pending' as const
      };
      
      const userKey = `fantasyUser_${selectedSeason}`;
      localStorage.setItem(userKey, JSON.stringify(newUserInfo));
      localStorage.setItem('fantasyUser', JSON.stringify(newUserInfo)); 
      
      setUserInfo(newUserInfo);
      setSuccess(`Successfully joined ${leagueName}!`);

      // Open Smooth Payment Modal
      setPaymentModalData({
        leagueId: selectedLeagueId,
        leagueName,
        userName: survivorName,
        email,
        season: selectedSeason,
        amount: ENTRY_FEE,
        paymentMethod
      });
      setPaymentReported(false);
      setShowPaymentModal(true);

      setSelectedLeagueId(''); setSurvivorName(''); setRealName(''); setEmail('');
    } catch (err: any) {
      setError(err.message || 'An error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportPayment = async () => {
    if (!paymentModalData) return;
    setIsReportingPayment(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${paymentModalData.leagueId}/members/${encodeURIComponent(paymentModalData.userName)}/report-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          paymentMethod: paymentModalData.paymentMethod,
          note: `Self-reported payment for Season ${paymentModalData.season} via ${paymentModalData.paymentMethod}`
        }),
      });
      if (!response.ok) throw new Error('Failed to record payment');
      setPaymentReported(true);
      
      // Update local storage user
      if (userInfo) {
        const updatedUser = { ...userInfo, paymentStatus: 'marked_paid' as const };
        setUserInfo(updatedUser);
        localStorage.setItem(`fantasyUser_${selectedSeason}`, JSON.stringify(updatedUser));
        localStorage.setItem('fantasyUser', JSON.stringify(updatedUser));
      }
    } catch (err: any) {
      console.error(err);
      alert('Could not notify commissioner, but your entry is saved.');
    } finally {
      setIsReportingPayment(false);
    }
  };

  const getPaymentDeeplink = (method: string, amount: number, memoName: string, season: number) => {
    const note = encodeURIComponent(`Survivor Season ${season} - ${memoName}`);
    switch (method) {
      case 'Venmo':
        return `https://venmo.com/${VENMO_USERNAME}?txn=pay&amount=${amount}&note=${note}`;
      case 'PayPal':
        return `https://paypal.me/${PAYPAL_USERNAME}/${amount}`;
      case 'CashApp':
        return `https://cash.app/$${CASHAPP_TAG}/${amount}`;
      default:
        return `https://venmo.com/${VENMO_USERNAME}?txn=pay&amount=${amount}&note=${note}`;
    }
  };

  const handleLoginSuccess = (loggedInUser: AuthUserInfo) => {
    const currentSeason = localStorage.getItem('selectedSeason') || selectedSeason.toString();
    localStorage.setItem(`fantasyUser_${currentSeason}`, JSON.stringify(loggedInUser));
    localStorage.setItem('fantasyUser', JSON.stringify(loggedInUser));
    setUserInfo(loggedInUser);
  };

  const handleLoginAs = (foundUser: FoundUser) => {
    const season = foundUser.season || 49;
    const fullUserInfo = {
      userName: foundUser.userName,
      survivorName: foundUser.survivorName,
      realName: foundUser.realName,
      leagueId: foundUser.leagueId,
      email: userInfo?.email || '',
      paymentMethod: 'Unknown' 
    };
    
    // Switch global season and persist user info
    localStorage.setItem('selectedSeason', season.toString());
    const userKey = `fantasyUser_${season}`;
    localStorage.setItem(userKey, JSON.stringify(fullUserInfo));
    localStorage.setItem('fantasyUser', JSON.stringify(fullUserInfo));
    
    // Notify app of season change
    window.dispatchEvent(new Event('seasonChanged'));
    
    setUserInfo(fullUserInfo);
    navigate(`/league/${foundUser.leagueId}`);
  };

  return (
    <Container className="mt-4 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-0 fw-bold">🏝️ Join / View League</h2>
          <small className="text-muted">Register for upcoming pools or manage your existing entries</small>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <span className="small fw-bold text-muted">Season:</span>
          {availableSeasons.map(s => (
            <Button
              key={s}
              size="sm"
              variant={selectedSeason === s ? "primary" : "outline-secondary"}
              onClick={() => handleSeasonSelect(s)}
              className="rounded-pill px-3 fw-bold"
            >
              Season {s} {s === Math.max(...availableSeasons) ? '🔥 (Upcoming)' : ''}
            </Button>
          ))}
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && (
        <Alert variant="success">
          {success}
          {userInfo && !isPreviewingAnotherSeason && <Link to={`/draft`} className="ms-2">Go to Draft Page</Link>}
        </Alert>
      )}

      {userInfo && userInfo.leagueId && (
        <Card className="shadow-sm home-card mb-4 border-success">
          <Card.Header as="h5" className="bg-success text-white">Currently Logged In: {userInfo.userName}</Card.Header>
          <Card.Body className="text-center">
            <p>You are viewing data for: <strong>Season {selectedSeason}</strong></p>
            <p>Your current league entry: <strong>{userEntries.find(entry => entry.leagueId === userInfo.leagueId)?.leagueName || 'Season ' + selectedSeason + ' League'}</strong></p>
            <div className="d-flex justify-content-center flex-wrap">
                {userInfo.leagueId && (
                  <Link to={`/league/${userInfo.leagueId}`} className="btn btn-primary m-1">View Standings</Link>
                )}
                {selectedSeason >= (availableSeasons.length > 0 ? Math.max(...availableSeasons) : 51) ? (
                  <Link to="/draft" className="btn btn-warning m-1">Manage Draft</Link>
                ) : (
                  <Link to={`/league/${userInfo.leagueId || ''}`} className="btn btn-outline-primary m-1">
                    Season {selectedSeason} Archives & Standings
                  </Link>
                )}
                <Button variant="outline-danger" className="m-1" onClick={() => {
                    localStorage.removeItem(`fantasyUser_${selectedSeason}`);
                    setUserInfo(null);
                }}>
                  Log Out of Season {selectedSeason}
                </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      <Row>
        <Col md={6} className="mb-4">
          <Card className="shadow-sm home-card h-100">
            <Card.Header as="h5">Join New League Entry (Season {selectedSeason})</Card.Header>
            <Card.Body>
              {leagues.length === 0 ? (
                <Alert variant="warning">
                  Joining is currently disabled for Season {selectedSeason}. Please contact the commissioner if you believe this is an error.
                </Alert>
              ) : (
                <Form onSubmit={handleJoinSubmit}>
                  <Form.Group className="mb-3" controlId="formLeagueSelect">
                    <Form.Label>Select League</Form.Label>
                    <Form.Select value={selectedLeagueId} onChange={(e) => setSelectedLeagueId(e.target.value)} required disabled={isLoading || leagues.length === 0}>
                      <option value="" disabled>-- Select a League --</option>
                      {leagues.map(league => <option key={league.id} value={league.id}>{league.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="formSurvivorName">
                    <Form.Label>Your Survivor Name</Form.Label>
                    <Form.Control type="text" placeholder="Enter your Survivor name" value={survivorName} onChange={(e) => setSurvivorName(e.target.value)} required disabled={isLoading} />
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="formRealName">
                    <Form.Label>Your Real Name (for record keeping)</Form.Label>
                    <Form.Control type="text" placeholder="Enter your real name" value={realName} onChange={(e) => setRealName(e.target.value)} required disabled={isLoading} />
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="formEmail">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isLoading} />
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="formPaymentMethod">
                    <Form.Label>Payment Method</Form.Label>
                    <div>
                      <Form.Check inline type="radio" label="Venmo" name="paymentMethod" value="Venmo" checked={paymentMethod === 'Venmo'} onChange={(e) => setPaymentMethod(e.target.value)} required disabled={isLoading} />
                      <Form.Check inline type="radio" label="PayPal" name="paymentMethod" value="PayPal" checked={paymentMethod === 'PayPal'} onChange={(e) => setPaymentMethod(e.target.value)} required disabled={isLoading} />
                      <Form.Check inline type="radio" label="CashApp" name="paymentMethod" value="CashApp" checked={paymentMethod === 'CashApp'} onChange={(e) => setPaymentMethod(e.target.value)} required disabled={isLoading} />
                      <Form.Check inline type="radio" label="Other" name="paymentMethod" value="Other" checked={paymentMethod === 'Other'} onChange={(e) => setPaymentMethod(e.target.value)} required disabled={isLoading} />
                    </div>
                  </Form.Group>
                  <Button variant="success" type="submit" disabled={isLoading || !selectedLeagueId || !survivorName.trim() || !realName.trim() || !email.trim() || !paymentMethod}>Join League</Button>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} className="mb-4">
          <Card className="shadow-sm home-card h-100">
            <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
              <span>Your Entries</span>
              {userInfo && (
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  onClick={() => {
                    localStorage.removeItem(`fantasyUser_${selectedSeason}`);
                    localStorage.removeItem('fantasyUser');
                    setUserInfo(null);
                  }}
                >
                  Log Out
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {!userInfo ? (
                <div>
                  <AuthLogin 
                    onLoginSuccess={handleLoginSuccess}
                    title="🔥 Sign In"
                    subtitle="Enter your Survivor Name or Email to load your league entries"
                  />
                </div>
              ) : userEntries.length > 0 ? (
                <div>
                  <div className="mb-2 small text-muted">
                    Logged in as <strong>{userInfo.userName || userInfo.email}</strong>. Click an entry to view standings:
                  </div>
                  <ListGroup className="mt-2">
                    {userEntries.map((user, index) => {
                      const currentAppSeason = availableSeasons.length > 0 ? Math.max(...availableSeasons) : 51;
                      const isCurrentSeasonEntry = Number(user.season) === currentAppSeason;
                      const status = user.paymentStatus || (user.userName === userInfo.userName ? userInfo.paymentStatus : 'pending');
                      return (
                        <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center py-3">
                          <div>
                            <div>
                              <strong>Season {user.season}</strong>: {user.userName} in <em>"{user.leagueName}"</em>
                            </div>
                            {isCurrentSeasonEntry && (
                              <div className="mt-1">
                                {status === 'verified' ? (
                                  <Badge bg="success" className="me-2">✓ Paid & Verified</Badge>
                                ) : status === 'marked_paid' ? (
                                  <Badge bg="info" className="me-2 text-dark">Payment Reported (Pending Review)</Badge>
                                ) : (
                                  <Badge bg="warning" className="me-2 text-dark">Payment Due (${ENTRY_FEE})</Badge>
                                )}
                                {status !== 'verified' && (
                                  <Button 
                                    variant="link" 
                                    size="sm" 
                                    className="p-0 text-primary small text-decoration-none"
                                    onClick={() => {
                                      setPaymentModalData({
                                        leagueId: user.leagueId,
                                        leagueName: user.leagueName,
                                        userName: user.userName,
                                        email: user.realName || userInfo.email,
                                        season: user.season,
                                        amount: ENTRY_FEE,
                                        paymentMethod: 'Venmo'
                                      });
                                      setPaymentReported(status === 'marked_paid');
                                      setShowPaymentModal(true);
                                    }}
                                  >
                                    💳 {status === 'marked_paid' ? 'View/Update Payment' : 'Pay Now'}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                          <Button variant="outline-primary" size="sm" onClick={() => handleLoginAs(user)}>
                            View &rarr;
                          </Button>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </div>
              ) : (
                <Alert variant="secondary" className="mb-0">
                  No entries found for <strong>{userInfo.email || userInfo.userName}</strong>. You can join an active league on the left.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="justify-content-md-center mt-4">
        <Col md={12}>
          <Card className="shadow-sm home-card">
            <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
              <span>💳 Instant Payment Channels (${ENTRY_FEE} Entry Fee)</span>
              <span className="small text-muted">Click any button for 1-tap pre-filled checkout</span>
            </Card.Header>
            <Card.Body>
              <Row className="g-3 text-center">
                <Col md={4}>
                  <Card className="p-3 border-0 bg-light h-100 shadow-sm">
                    <h6 className="fw-bold text-primary mb-2">Venmo (@{VENMO_USERNAME})</h6>
                    <p className="small text-muted mb-3">Pre-fills ${ENTRY_FEE} & memo on mobile and web.</p>
                    <a 
                      href={`https://venmo.com/${VENMO_USERNAME}?txn=pay&amount=${ENTRY_FEE}&note=Survivor%20Season%20${selectedSeason}%20Pool`}
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn btn-primary btn-sm rounded-pill fw-bold"
                    >
                      Pay ${ENTRY_FEE} with Venmo
                    </a>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="p-3 border-0 bg-light h-100 shadow-sm">
                    <h6 className="fw-bold text-info text-dark mb-2">PayPal (@{PAYPAL_USERNAME})</h6>
                    <p className="small text-muted mb-3">Instant checkout with balance or credit card.</p>
                    <a 
                      href={`https://paypal.me/${PAYPAL_USERNAME}/${ENTRY_FEE}`}
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn btn-info btn-sm text-white rounded-pill fw-bold"
                    >
                      Pay ${ENTRY_FEE} with PayPal
                    </a>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="p-3 border-0 bg-light h-100 shadow-sm">
                    <h6 className="fw-bold text-success mb-2">CashApp (${CASHAPP_TAG})</h6>
                    <p className="small text-muted mb-3">Direct 1-tap transfer via CashApp.</p>
                    <a 
                      href={`https://cash.app/$${CASHAPP_TAG}/${ENTRY_FEE}`}
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn btn-success btn-sm rounded-pill fw-bold"
                    >
                      Pay ${ENTRY_FEE} with CashApp
                    </a>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Instant Payment Confirmation Modal */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-dark text-white">
          <Modal.Title className="fw-bold">🏝️ Complete Your Season {paymentModalData?.season} Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {paymentModalData && (
            <div>
              <Alert variant="success" className="d-flex align-items-center">
                <div>
                  <h6 className="alert-heading mb-1 fw-bold">Entry Confirmed for {paymentModalData.userName}!</h6>
                  <p className="mb-0 small">
                    League: <strong>{paymentModalData.leagueName}</strong> &bull; Buy-In: <strong>${paymentModalData.amount}</strong>
                  </p>
                </div>
              </Alert>

              <div className="mb-4">
                <h6 className="fw-bold text-secondary mb-3">STEP 1: Send Your ${paymentModalData.amount} Buy-In</h6>
                <div className="d-flex gap-2 flex-wrap mb-3">
                  <Button 
                    variant={paymentModalData.paymentMethod === 'Venmo' ? 'primary' : 'outline-primary'}
                    onClick={() => setPaymentModalData({ ...paymentModalData, paymentMethod: 'Venmo' })}
                    className="fw-bold"
                  >
                    Venmo (@{VENMO_USERNAME})
                  </Button>
                  <Button 
                    variant={paymentModalData.paymentMethod === 'PayPal' ? 'info' : 'outline-info'}
                    onClick={() => setPaymentModalData({ ...paymentModalData, paymentMethod: 'PayPal' })}
                    className={paymentModalData.paymentMethod === 'PayPal' ? 'text-white fw-bold' : 'fw-bold'}
                  >
                    PayPal (@{PAYPAL_USERNAME})
                  </Button>
                  <Button 
                    variant={paymentModalData.paymentMethod === 'CashApp' ? 'success' : 'outline-success'}
                    onClick={() => setPaymentModalData({ ...paymentModalData, paymentMethod: 'CashApp' })}
                    className="fw-bold"
                  >
                    CashApp (${CASHAPP_TAG})
                  </Button>
                </div>

                <div className="p-3 bg-light rounded-3 border text-center">
                  <div className="mb-2">
                    <span className="badge bg-secondary mb-2">1-Tap Direct Checkout</span>
                    <h5 className="fw-bold mb-1">
                      {paymentModalData.paymentMethod === 'Venmo' && `Venmo: @${VENMO_USERNAME}`}
                      {paymentModalData.paymentMethod === 'PayPal' && `PayPal: @${PAYPAL_USERNAME}`}
                      {paymentModalData.paymentMethod === 'CashApp' && `CashApp: $${CASHAPP_TAG}`}
                    </h5>
                    <p className="text-muted small mb-3">
                      Amount: <strong>${paymentModalData.amount}</strong> | Memo: <em>Survivor Season {paymentModalData.season} - {paymentModalData.userName}</em>
                    </p>
                  </div>

                  <a 
                    href={getPaymentDeeplink(paymentModalData.paymentMethod, paymentModalData.amount, paymentModalData.userName, paymentModalData.season)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-lg btn-success px-4 py-2 rounded-pill fw-bold shadow-sm"
                  >
                    🚀 Open {paymentModalData.paymentMethod} App & Pay ${paymentModalData.amount}
                  </a>
                </div>
              </div>

              <div className="border-top pt-3">
                <h6 className="fw-bold text-secondary mb-2">STEP 2: Confirm Payment Sent</h6>
                <p className="small text-muted mb-3">
                  Once you have sent your payment, click the button below to notify the commissioner automatically so you don't have to send a manual message.
                </p>

                {paymentReported ? (
                  <Alert variant="success" className="mb-0 text-center py-2">
                    ✓ <strong>Payment Reported!</strong> The commissioner has been notified. You are ready to draft!
                  </Alert>
                ) : (
                  <Button 
                    variant="warning" 
                    className="w-100 py-2 fw-bold"
                    onClick={handleReportPayment}
                    disabled={isReportingPayment}
                  >
                    {isReportingPayment ? 'Recording...' : `✓ I Have Sent My $${paymentModalData.amount} Payment`}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              setShowPaymentModal(false);
              navigate('/draft');
            }}
          >
            Go to Draft Page &rarr;
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="text-center mt-4">
          <Link to="/" className="btn btn-secondary">Back to Home</Link>
      </div>
    </Container>
  );
};

export default LeagueJoin;
